import React, { useState } from "react";
import { platformAPI } from "../services/api";

/**
 * Deleting a shop its owner abandoned. Scheduling locks it at once (its people see
 * the message, its menu says "not available") and nothing is removed for 30 days,
 * during which it can be restored exactly as it was. Then the hourly job empties
 * it: data, members, devices; its link is freed. Payments and receipts are kept.
 */
export default function ShopDeletionTab({ shop, onChange }) {
  const [reason, setReason] = useState("");
  const [message, setMessage] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const run = async (action) => {
    setBusy(true);
    setError("");
    try {
      await action();
      onChange();
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || "That did not work");
    } finally {
      setBusy(false);
    }
  };

  if (shop.deleted_at) {
    return (
      <p className="text-sm text-gray-600">
        Deleted on {new Date(shop.deleted_at).toLocaleString()}. Its payments and receipts are kept.
      </p>
    );
  }

  if (shop.deletion_scheduled_at) {
    return (
      <div className="max-w-xl rounded-xl border border-red-200 bg-red-50 p-5 text-sm text-red-900 space-y-3">
        <p>
          Scheduled for deletion on {new Date(shop.deletion_scheduled_at).toLocaleString()}. It will be purged
          after <strong>{new Date(shop.deletion_purge_after).toLocaleString()}</strong>.
        </p>
        {shop.deletion_reason && <p>Reason: {shop.deletion_reason}</p>}
        <button
          type="button"
          disabled={busy}
          onClick={() => run(() => platformAPI.cancelDeletion(shop.id))}
          className="rounded-lg bg-white border border-red-300 px-4 py-2 font-semibold text-red-700 hover:bg-red-100 disabled:opacity-50"
        >
          Restore the shop
        </button>
        {error && <p className="text-red-700">{error}</p>}
      </div>
    );
  }

  const input = "w-full px-3 py-2 border border-gray-300 rounded-lg";

  return (
    <form
      className="max-w-xl bg-white rounded-xl shadow p-5 space-y-4"
      onSubmit={(event) => {
        event.preventDefault();
        run(() => platformAPI.scheduleDeletion(shop.id, { confirm, reason, message: message || undefined }));
      }}
    >
      <div className="text-sm text-gray-700 space-y-1">
        <p>
          The shop is locked straight away and <strong>deleted after 30 days</strong>; until then it can be restored
          as it was. Deleting removes its products, sales, stock, staff and devices and frees its link. Payments and
          receipts are kept. The owner's account stays (it may own another shop or buy as a customer).
        </p>
        {shop.pack_member_count > 0 && (
          <p className="text-red-700">This shop pays for other shops: take them out of its pack first.</p>
        )}
      </div>
      <label className="block text-sm">
        <span className="font-medium text-gray-700">Why (for the audit log)</span>
        <input className={input} value={reason} onChange={(e) => setReason(e.target.value)} required />
      </label>
      <label className="block text-sm">
        <span className="font-medium text-gray-700">Message the owner sees if they sign in (optional)</span>
        <input
          className={input}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="This shop's account is being closed. Contact us if this is a mistake."
        />
      </label>
      <label className="block text-sm">
        <span className="font-medium text-gray-700">
          Type <code>{shop.slug}</code> to confirm
        </span>
        <input className={input} value={confirm} onChange={(e) => setConfirm(e.target.value)} required />
      </label>
      {error && <p className="text-red-600 text-sm">{error}</p>}
      <button
        type="submit"
        disabled={busy || confirm.trim().toLowerCase() !== shop.slug}
        className="rounded-lg bg-red-600 px-4 py-2 font-semibold text-white hover:bg-red-700 disabled:opacity-50"
      >
        Schedule deletion
      </button>
    </form>
  );
}
